import { useState } from "react";
import { cn } from "@/lib/cn";
import { initialsFor } from "@/lib/initials";

// Design evaluation F15: user rows loaded a generated avatar from Vercel per email,
// which sent every user's email to a third party, broke under CSP or air-gap,
// and painted an off-palette gradient. This draws the avatar locally: the
// user's own picture_url when they have one (falling back if it fails to load),
// otherwise their initials on the role-tag token pair.
//
// Decorative by contract: every caller shows the name as text beside it, so the
// avatar is aria-hidden and a screen reader does not hear the name twice.

const SIZES = {
  sm: "h-7 w-7 text-xs",
  md: "h-8 w-8 text-xs",
  lg: "h-10 w-10 text-sm",
} as const;

export interface InitialsAvatarProps {
  name?: string | null;
  email?: string | null;
  /** The user's own picture. Shown when set and loadable; never a generated URL. */
  src?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}

export function InitialsAvatar({ name, email, src, size = "lg", className }: InitialsAvatarProps) {
  const [failed, setFailed] = useState<string | null>(null);
  const box = cn("flex-shrink-0 rounded-full", SIZES[size], className);

  if (src && failed !== src) {
    return <img src={src} alt="" aria-hidden="true" className={box} onError={() => setFailed(src)} />;
  }
  return (
    <span
      aria-hidden="true"
      data-testid="initials-avatar"
      className={cn(
        box,
        "inline-flex items-center justify-center select-none font-semibold leading-none",
        "bg-[var(--accent-tag-bg)] text-[var(--accent-tag-text)]",
      )}
    >
      {initialsFor(name, email)}
    </span>
  );
}
